import { useCallback, useState } from 'react'
import styled, { css } from 'styled-components'
import translate from '@i18n'

import useViewportTracking from '@hooks/useViewportTracking'
import useTracking, { TrackingFnType } from '@hooks/useTracking'

import PrimaryCTAButton from '@components/Buttons/PrimaryCTAButton'
import FollowMyTeamSportCompetitorsList from '../FollowMyTeamSelectTeam/FollowMyTeamSportCompetitorsList'
import FollowMyTeamDrawerHeader from '../FollowMyTeamDrawer/Header'
import FollowMyTeamMyTeamsList from './FollowMyTeamMyTeamsList'
import FmTOnboardingContainer from '@components/FmtOnboardingContainer/FmTOnboardingContainer'

const Container = styled.div`
  ${({
    theme: {
      spacing: { spacing16 },
    },
  }) => css`
    display: flex;
    flex-direction: column;
    > * {
      padding-left: ${spacing16};
      padding-right: ${spacing16};
    }
  `}
`

const Wrapper = styled.div`
  ${({
    theme: {
      spacing: { spacing16 },
    },
  }) => css`
    padding-top: ${spacing16};
    display: flex;
    flex-direction: column;
    overflow-y: auto;
    max-height: 100vh;
  `}
`

const StyledPrimaryCTAButton = styled(PrimaryCTAButton)`
  width: 100%;
`

const FollowMyTeamMyTeams = ({ onClose }: { onClose: () => void }) => {
  const [showSportCompetitorsList, setShowSportCompetitorsList] =
    useState(false)

  const handleBack = useCallback(() => {
    setShowSportCompetitorsList(false)
  }, [])

  const handleAddTeam = useCallback(() => {
    setShowSportCompetitorsList(true)
  }, [])

  const onMyTeamsImpressionEvent = useCallback<TrackingFnType>(
    () => ({
      event: 'my_teams_impression',
    }),
    []
  )

  const onSelectTeamImpressionEvent = useCallback<TrackingFnType>(
    () => ({
      event: 'select_team_impression',
    }),
    []
  )

  const trackedOnMyTeamsImpression = useTracking(onMyTeamsImpressionEvent)
  const trackedOnSelectTeamImpression = useTracking(onSelectTeamImpressionEvent)

  const myTeamsViewportRef = useViewportTracking({
    track: true,
    onImpression: trackedOnMyTeamsImpression,
  })

  const selectTeamViewportRef = useViewportTracking({
    track: true,
    onImpression: trackedOnSelectTeamImpression,
  })

  return (
    <Container>
      <FollowMyTeamDrawerHeader
        title={translate(
          showSportCompetitorsList
            ? 'followMyTeam.selectTeam'
            : 'followMyTeam.myTeams'
        )}
        showBackButton={showSportCompetitorsList}
        onBack={handleBack}
        onClose={onClose}
      />
      {showSportCompetitorsList ? (
        <Wrapper ref={selectTeamViewportRef}>
          <FmTOnboardingContainer position="selectTeam" />
          <FollowMyTeamSportCompetitorsList />
        </Wrapper>
      ) : (
        <Wrapper ref={myTeamsViewportRef}>
          <StyledPrimaryCTAButton
            onClick={handleAddTeam}
            colorVariant="black"
            size="large"
            iconName="plus-circle"
            iconLeft={true}>
            {translate('followMyTeamOverview.addTeam')}
          </StyledPrimaryCTAButton>
          <FollowMyTeamMyTeamsList />
        </Wrapper>
      )}
    </Container>
  )
}

export default FollowMyTeamMyTeams
